import React, { useEffect, useState } from 'react';
import { BellAlertIcon, XMarkIcon } from '@heroicons/react/24/outline';
import { toast } from 'react-hot-toast';
import universalNotificationService from '../services/universalNotificationService';
import { useAuthStore } from '../stores/authStore';

/**
 * Делает так, чтобы push-уведомления реально включались.
 *  - Разрешение уже выдано → тихо получаем FCM-токен и регистрируем на
 *    сервере (раз за сессию браузера: токены протухают и меняются).
 *  - Разрешение не спрашивали → полоса «Включите уведомления» с кнопкой
 *    (браузеры дают запросить разрешение только по клику).
 *  - Запрещено → полоса с подсказкой, как включить в настройках сайта.
 * Закрытие полосы прячет её до завтра.
 */
const DISMISS_KEY = 'ggdesk:notifPromptDismissedUntil';
const SESSION_KEY = 'ggdesk:fcmRegisteredThisSession';

type State = 'hidden' | 'ask' | 'denied';

const NotificationPrompt: React.FC = () => {
  const { user } = useAuthStore();
  const [state, setState] = useState<State>('hidden');
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!user || typeof window === 'undefined' || typeof Notification === 'undefined') return;
    let alive = true;
    universalNotificationService.ready().then(async () => {
      if (!alive || !universalNotificationService.isSupported()) return;
      const perm = Notification.permission;
      if (perm === 'granted') {
        try {
          if (sessionStorage.getItem(SESSION_KEY) !== '1') {
            const ok = await universalNotificationService.subscribe();
            if (ok) sessionStorage.setItem(SESSION_KEY, '1');
          }
        } catch { /* ignore */ }
        return;
      }
      let until = 0;
      try { until = Number(localStorage.getItem(DISMISS_KEY) || 0); } catch { /* ignore */ }
      if (Date.now() < until) return;
      setState(perm === 'denied' ? 'denied' : 'ask');
    });
    return () => { alive = false; };
  }, [user]);

  const dismiss = () => {
    try { localStorage.setItem(DISMISS_KEY, String(Date.now() + 24 * 3600 * 1000)); } catch { /* ignore */ }
    setState('hidden');
  };

  const enable = async () => {
    setBusy(true);
    try {
      const ok = await universalNotificationService.subscribe();
      if (ok) {
        try { sessionStorage.setItem(SESSION_KEY, '1'); } catch { /* ignore */ }
        toast.success('Уведомления включены');
        setState('hidden');
      } else if (Notification.permission === 'denied') {
        setState('denied');
      } else {
        toast.error('Не удалось включить уведомления. Попробуйте ещё раз или другой браузер.');
      }
    } finally {
      setBusy(false);
    }
  };

  if (state === 'hidden') return null;

  return (
    <div className="flex items-center gap-3 px-4 py-2 border-b border-amber-500/30 bg-amber-500/10 text-sm text-foreground">
      <BellAlertIcon className="h-5 w-5 shrink-0 text-amber-600" />
      {state === 'ask' ? (
        <>
          <span className="flex-1">Включите уведомления — новые заявки и сообщения клубов будут приходить, даже когда вкладка закрыта.</span>
          <button
            type="button"
            onClick={enable}
            disabled={busy}
            className="shrink-0 h-8 px-3 rounded-md bg-primary text-primary-foreground hover:bg-primary/90 disabled:opacity-60"
          >
            {busy ? 'Включаем…' : 'Включить'}
          </button>
        </>
      ) : (
        <span className="flex-1">
          Уведомления запрещены в браузере. Нажмите на значок замка слева от адреса ggdesk.ru → «Уведомления» → «Разрешить», затем обновите страницу.
        </span>
      )}
      <button type="button" onClick={dismiss} className="shrink-0 p-1 rounded text-muted-foreground hover:text-foreground" aria-label="Скрыть до завтра" title="Скрыть до завтра">
        <XMarkIcon className="h-4 w-4" />
      </button>
    </div>
  );
};

export default NotificationPrompt;
